<template>
  <footer class="text-black bg-white dark:bg-zinc-950 dark:text-white">
    <!-- Loading State -->
    <div v-if="pending" class="grid grid-cols-1 gap-5 p-4 md:grid-cols-4">
      <div
        v-for="i in 4"
        :key="i"
        class="flex flex-col gap-2 pt-2 border-t-2 border-black dark:border-white"
      >
        <Skeleton class="w-32 h-6" />
        <Skeleton class="w-full h-4" />
        <Skeleton class="w-3/4 h-4" />
      </div>
    </div>

    <!-- Content -->
    <div
      v-else-if="footerLinksData && !error"
      class="grid grid-cols-1 gap-5 p-4 md:grid-cols-4"
    >
      <!-- Column 1: Address and Contact Details -->
      <div
        class="flex flex-col pt-2 border-t-2 border-black dark:border-white md:gap-2"
      >
        <RouterLink to="/">
          <NuxtImg
            src="/images/logos/HighfieldLogos/svg/highfield_long_black.svg"
            class="w-32 dark:hidden"
            :placeholder="[50, 25, 75, 5]"
          />
          <NuxtImg
            src="/images/logos/HighfieldLogos/svg/highfield_long_white.svg"
            class="hidden w-32 dark:block"
            :placeholder="[50, 25, 75, 5]"
            loading="lazy"
          />
        </RouterLink>
        <div class="max-w-xs text-base leading-relaxed font-inter">
          <div
            class="relative pt-4 before:content-[''] before:absolute before:top-0 before:left-0 before:w-[128px] before:h-[2px] before:bg-black dark:before:bg-white"
          >
            <span v-for="(line, index) in getHighfieldContent()" :key="index">
              {{ line }}<br />
            </span>
          </div>
        </div>
      </div>

      <!-- Column 2: Links -->
      <div
        class="flex flex-col gap-2 pt-2 border-t-2 border-black dark:border-white font-inter"
      >
        <div v-for="(button, index) in getLinksContent()" :key="index">
          <FooterButton :button="button" />
        </div>
      </div>

      <!-- Column 3: Policies -->
      <div
        class="flex flex-col gap-2 pt-2 border-t-2 border-black dark:border-white font-inter"
      >
        <div v-for="(button, index) in getPoliciesContent()" :key="index">
          <FooterButton :button="button" />
        </div>
      </div>

      <!-- Column 4: Social Links -->
      <div
        class="flex flex-col gap-2 pt-2 border-t-2 border-black dark:border-white font-inter"
      >
        <span v-for="(line, index) in getContactContent()" :key="index"
          >{{ line }}<br
        /></span>

        <div class="flex mt-2 md:mt-4">
          <ul class="flex items-center gap-4">
            <li v-for="link in links" :key="link.icon" class="group">
              <a :href="link.link" target="_blank" rel="noopener noreferrer">
                <Icon
                  :name="link.icon"
                  class="text-[28px] w-7 h-7 transition-transform duration-300 group-hover:rotate-12"
                />
              </a>
            </li>
          </ul>
        </div>
        <div class="uppercase">HighField © {{ new Date().getFullYear() }}</div>
        <div>{{ $t("rightsReserved") }}</div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="p-4 text-center text-red-500">
      {{ error?.message || "Erro ao carregar links do footer" }}
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";
import { useFooterLinks } from "~/composables/useFooterLinks";
import Skeleton from "~/components/ui/skeleton/Skeleton.vue";

const { footerLinks, pending, error } = useFooterLinks();

// Usar dados do endpoint do Supabase
const footerLinksData = computed(() => {
  return footerLinks.value || [];
});

// Helpers para extrair dados específicos das seções
const getHighfieldContent = () => {
  const section = footerLinksData.value?.[0];
  if (!section?.content) return [];
  // Extrair apenas strings (primeiros 4 items)
  return section.content
    .filter((item: any, index: number) => index < 4)
    .map((item: any) => (typeof item === "string" ? item : item.label || ""));
};

const getLinksContent = () => {
  const section = footerLinksData.value?.[1];
  if (!section?.content) return [];
  // Retornar objetos com label, to, class, active
  return section.content.map((item: any) => {
    if (typeof item === "string") {
      return { label: item, to: undefined, active: true };
    }
    return {
      label: item.label || item,
      to: item.to || undefined,
      class: item.class || "",
      active: item.active !== false,
    };
  });
};

const getPoliciesContent = () => {
  const section = footerLinksData.value?.[2];
  if (!section?.content) return [];
  // Retornar objetos com label, to, class, active
  return section.content.map((item: any) => {
    if (typeof item === "string") {
      return { label: item, to: undefined, active: true };
    }
    return {
      label: item.label || item,
      to: item.to || undefined,
      class: item.class || "",
      active: typeof item.active === "boolean" ? item.active : true,
    };
  });
};

const getContactContent = () => {
  const section = footerLinksData.value?.[0];
  if (!section?.content) return [];
  // Extrair apenas strings (últimos 2 items: email e instrução)
  return section.content
    .filter((item: any, index: number) => index >= 4)
    .map((item: any) => (typeof item === "string" ? item : item.label || ""));
};

const links: { name: string; link: string; icon: string }[] = [
  { name: "Ligar", link: "tel:+351967998541", icon: "famicons:call-sharp" },
  {
    name: "Email",
    link: "mailto:studio@highfield.pt",
    icon: "material-symbols:mail",
  },
  {
    name: "Instagram",
    link: "https://www.instagram.com/highfield.pt/",
    icon: "ri:instagram-fill",
  },
  {
    name: "Facebook",
    link: "https://www.facebook.com/highfield.pt",
    icon: "simple-icons:facebook",
  },
  {
    name: "Discord",
    link: "https://discord.gg/uA24BR6nz5",
    icon: "ic:baseline-discord",
  },
  {
    name: "Linktree",
    link: "https://linktr.ee/highfield.pt",
    icon: "simple-icons:linktree",
  },
];
</script>
